import { useMemo, useState } from 'react';
import { useOrganizationContext } from '../../service/context/organizationContext';
import ModalComponent from '../modal/modal';
import styles from './selectPlayerModal.module.css';
import { usePlayerSummary } from '../../hooks/usePlayerSummary';
import { PlayerSummary } from '@inplay/models';
import RadioGroupV2 from '../radioGroup';
import { PlayerType, SelectPlayerProps } from './selectPlayerModal.types';
import { Trans, useTranslation } from 'react-i18next';
import PlayerCard from './playerCard';
import ExistingPlayer from './existingPlayer';
import NewPlayer from './newPlayer';

const SelectPlayerModal = (props: SelectPlayerProps) => {
  const { selectedVenueBranch } = useOrganizationContext();

  const selectedBranchIDs: string[] = useMemo<string[]>(() => {
    return selectedVenueBranch ? [selectedVenueBranch._id] : [];
  }, [selectedVenueBranch]);

  const { allPlayers, playerLoading } = usePlayerSummary(
    selectedBranchIDs,
    undefined,
    undefined,
    undefined,
    undefined,
    false,
  );

  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSummary>();
  const [playerType, setPlayerType] = useState<PlayerType>(
    PlayerType.NewPlayer,
  );
  const { t } = useTranslation();
  const [newPlayerImages, setNewPlayerImages] = useState<{
    portraitImage: string;
    fullBodyImage: string;
  }>({ portraitImage: '', fullBodyImage: '' });

  const [query, setQuery] = useState<string>('');

  const filteredPlayers: PlayerSummary[] = useMemo(() => {
    if (!query.trim()) return allPlayers;
    const lowerQuery: string = query.toLowerCase();
    return allPlayers.filter((player) =>
      player.playerId.toLowerCase().includes(lowerQuery),
    );
  }, [allPlayers, query]);

  const isProceedDisabled: boolean =
    playerType === PlayerType.ExistingPlayer
      ? !selectedPlayer
      : !newPlayerImages.portraitImage || !newPlayerImages.fullBodyImage;

  return (
    <div className={styles.selectPlayerOverlay}>
      <ModalComponent
        open
        onToggle={props.onClose}
        title={<Trans i18nKey="selectPlayer.selectPlayer" />}
        centered
        noHeaderBorder
        isShowClose
        onClose={props.onClose}
        showFooter1Button
        footerButton1Text={<Trans i18nKey="selectPlayer.proceed" />}
        footerClassName={styles.modalFooter}
        footerButton1WrapperStyle={styles.proceedButtonWrapper}
        footerButton1Style={styles.proceedButton}
        disableFooter1Button={isProceedDisabled}
        onFooterButton1Click={() => {
          if (playerType === PlayerType.ExistingPlayer && selectedPlayer) {
            props.onProceed(selectedPlayer, playerType);
          } else if (playerType === PlayerType.NewPlayer) {
            props.onProceed(newPlayerImages, playerType);
          }
        }}
      >
        <div className={styles.modalBody}>
          <div className={styles.left}>
            <span>
              <Trans i18nKey="selectPlayer.description" />
            </span>

            <RadioGroupV2
              horizontal
              name="playerType"
              value={playerType}
              onChange={(value: string) => setPlayerType(value as PlayerType)}
              options={[
                {
                  label: t('selectPlayer.newPlayer'),
                  value: PlayerType.NewPlayer,
                },
                {
                  label: t('selectPlayer.existingPlayer'),
                  value: PlayerType.ExistingPlayer,
                },
              ]}
            />

            {playerType === PlayerType.ExistingPlayer && selectedPlayer && (
              <PlayerCard
                player={selectedPlayer}
                style={styles.selectedPlayerPreview}
              />
            )}
          </div>

          <div className={styles.separator} />

          <div className={styles.right}>
            {playerType === PlayerType.ExistingPlayer ? (
              <ExistingPlayer
                playerLoading={playerLoading}
                query={query}
                setQuery={setQuery}
                filteredPlayers={filteredPlayers}
                setSelectedPlayer={setSelectedPlayer}
              />
            ) : (
              <NewPlayer
                images={newPlayerImages}
                setImages={setNewPlayerImages}
              />
            )}
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default SelectPlayerModal;
